import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import apiClient from "../api/apiClient";
import { useLicense } from "./licenseContext";
import { useAppState } from "./appStateContext";

const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
    const { currentLicense } = useLicense();
    const { user } = useAppState();

    // currentLocation: the selected location object ({ locationId, locationName, licenseNo, ... })
    // null = "All Locations" / license-level view
    const [currentLocation, setCurrentLocation] = useState(() => {
        try {
            const saved = localStorage.getItem("currentLocation");
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    });

    // All locations available under the current license
    const [locations, setLocations] = useState([]);

    // Fetch locations whenever the active license changes
    const fetchLocations = useCallback(async (licenseNo) => {
        if (!licenseNo) {
            setLocations([]);
            return;
        }
        try {
            const res = await apiClient.get(`/locations?licenseNo=${licenseNo}`);
            const active = (res.data || []).filter((l) => l.active);
            setLocations(active);

            // If the stored currentLocation belongs to a different license, clear it
            setCurrentLocation((prev) => {
                if (prev && prev.licenseNo !== licenseNo) {
                    localStorage.removeItem("currentLocation");
                    return null;
                }
                return prev;
            });
        } catch (err) {
            console.error("[LocationContext] Failed to fetch locations:", err);
            setLocations([]);
        }
    }, []);

    const isAuthenticated = !!user;
    useEffect(() => {
        if (!isAuthenticated) return;
        fetchLocations(currentLicense?.licenseNo);
    }, [isAuthenticated, currentLicense?.licenseNo, fetchLocations]);

    // Auto-select first location for scoped users who have no active selection
    useEffect(() => {
        const isScoped = currentLicense?.location_scoped === 1;
        if (isScoped && !currentLocation && locations.length > 0) {
            setCurrentLocation(locations[0]);
        }
    }, [locations, currentLicense, currentLocation]);

    // Persist currentLocation to localStorage whenever it changes
    useEffect(() => {
        if (currentLocation) {
            localStorage.setItem("currentLocation", JSON.stringify(currentLocation));
        } else {
            localStorage.removeItem("currentLocation");
        }
    }, [currentLocation]);

    const refreshLocations = useCallback((licenseNo) => {
        return fetchLocations(licenseNo || currentLicense?.licenseNo);
    }, [fetchLocations, currentLicense?.licenseNo]);

    return (
        <LocationContext.Provider
            value={{
                currentLocation,
                setCurrentLocation,
                locations,
                setLocations,
                refreshLocations,
            }}
        >
            {children}
        </LocationContext.Provider>
    );
};

export const useLocation_ = () => useContext(LocationContext);
